import { Info } from '@ui/components/icons';
import { TooltipWithTouch } from '@ui/components/TooltipWithTouch';
import {
  getMarketTiming,
  MarketTiming,
} from '@ui/features/RWA/utils/getMarketTiming';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { useDataChainId } from '@ui/hooks/datachain';
import { useCountdown } from '@ui/hooks/useCountdown';
import { Icon } from '@ui/lib/park/ui/icon';
import { Link } from '@ui/lib/park/ui/link';
import { Text } from '@ui/lib/park/ui/text';
import { useTranslation } from 'next-i18next';
import { ReactNode } from 'react';
import { HStack } from 'styled-system/jsx';

import { MARKET_CLOSE_GITBOOK_LINK } from '../config';
import { usePairMarketScheduleStore } from '../hooks/usePairMarketScheduleStore/usePairMarketScheduleStore';

export const PairMarketCountDown = () => {
  const pair = useTradingPair((s) => s.useTradingPair());
  const chainId = useDataChainId();
  const pairSchedule = usePairMarketScheduleStore((v) => {
    return v.getMarketScheduleByPair(pair.pairName, chainId);
  });

  const { t } = useTranslation();
  const status = getMarketTiming(
    pairSchedule.isClientOpen ?? true,
    pairSchedule.nextClose,
  );

  switch (status) {
    case MarketTiming.IS_OPEN:
      return null;
    case MarketTiming.IS_OPEN_ABOUT_TO_CLOSE:
      return (
        <HStack justifyContent="space-between">
          <MarketTooltip>
            <Text textStyle="text.xsmall" color="greyWhite.lightGrey3">
              {t('common.Market is about to close')}

              <Icon ml={1} boxSize="10px">
                <Info />
              </Icon>
            </Text>
          </MarketTooltip>

          {pairSchedule.nextClose && (
            <CountDownMarketClose to={pairSchedule.nextClose} />
          )}
        </HStack>
      );
    case MarketTiming.IS_CLOSE:
      return (
        <HStack justifyContent="space-between">
          <MarketTooltip>
            <Text textStyle="text.xsmall" color="greyWhite.lightGrey3">
              {t('common.Market is closed')}
              <Icon ml={1} boxSize="10px">
                <Info />
              </Icon>
            </Text>
          </MarketTooltip>

          {pairSchedule.nextOpen && (
            <CountDownMarketOpen to={pairSchedule.nextOpen} />
          )}
        </HStack>
      );
  }
};

const CountDownMarketClose = ({ to }: { to: Date }) => {
  const { minStr } = useCountdown({
    to: to,
  });
  const { t } = useTranslation();

  return (
    <Text textStyle="headings.xxsmall" color="primary.orangeLighter">
      {t('common.Please confirm order in {countDown}', {
        countDown: minStr,
      })}
    </Text>
  );
};

const CountDownMarketOpen = ({ to }: { to: Date }) => {
  const { minStr } = useCountdown({
    to: to,
  });
  const { t } = useTranslation();

  return (
    <Text textStyle="headings.xxsmall" color="white">
      {t('common.Open in {countDown}', {
        countDown: minStr,
      })}
    </Text>
  );
};

const MarketTooltip = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();

  return (
    <TooltipWithTouch.Root
      variant="darkBox"
      positioning={{
        placement: 'top',
      }}
    >
      <TooltipWithTouch.Positioner>
        <TooltipWithTouch.Content maxW="56" textStyle="text.small">
          <TooltipWithTouch.Arrow>
            <TooltipWithTouch.ArrowTip />
          </TooltipWithTouch.Arrow>
          <Text>
            {t(
              'common.Positions cannot be opened or closed outside market hours.',
            )}
          </Text>
          <Link href={MARKET_CLOSE_GITBOOK_LINK} target="_blank" mt={2}>
            {t('common.Learn more')}
          </Link>
        </TooltipWithTouch.Content>
      </TooltipWithTouch.Positioner>
      <TooltipWithTouch.Trigger>{children}</TooltipWithTouch.Trigger>
    </TooltipWithTouch.Root>
  );
};
